<template>
    <grid
        ref="grid"
        v-if="resortLockAccount"
        :column-defs="columnsDefs"
        :row-data="resortLockProperties"
        :is-auto-height="false"
    >
        <responsive-button
            :icon="['fas', 'user']"
            :clickable="false"
            :responsiveness="false"
            variant="link"
        >{{ accountEmailTruncated }}</responsive-button>
        <div class="break d-sm-block d-md-none" />
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            @click="handleSyncResortLockPropertiesClick"
            variant="primary"
        >Fetch LockState properties</responsive-button>
        <responsive-button
            :icon="['fas', 'gear']"
            @click="handleSettingsClick"
            variant="primary"
        >Settings</responsive-button>
        <responsive-button
            :icon="['fas', 'right-from-bracket']"
            @click="handleDisconnectClick"
            variant="primary"
        >Disconnect</responsive-button>
         <responsive-button
            :icon="['fas', 'plus']"
            @click="handleFetchLodgixPropertiesClick"
            variant="primary"
        >Fetch Lodgix properties</responsive-button>
        <disconnect-modal
            title="Disconnect LockState account"
            description="Are you sure you want to disconnect your LockState account? You will not be able to generate lock codes for your reservations any more."
            :modal-id="disconnectModalID"
            :disconnect="disconnect"
        />
        <fetch-lodgix-properties-modal
            account="LockState"
            :modal-id="fetchLodgixPropertiesModalID"
            :key="fetchLodgixPropertiesModalKey"
            :process="createProperty"
            @success-save="load"
        />
        <settings-modal-form
            :modal-id="settingsModalID"
        />
        <resort-lock-property-detail-modal-form
            :modal-id="updatePropertyModalID"
            :initial-values="currentResortLockPropertyInitialValues"
            :update="updateResortLockProperty"
            @success-save="handleUpdateResortLockPropertySuccess"
        />
        <resort-lock-lock-list-modal
            :modal-id="lockListModalID"
            :property-id="currentResortLockPropertyID"
            :property-name="currentResortLockPropertyName"
            @hide="handleResortLockLockListModalHide"
        />
    </grid>
    <div v-else-if="busy"></div>
    <div v-else></div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../../../api';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import {
        DOOR_LOCKS_RESORT_LOCK_PROPERTIES_ENDPOINT,
        DOOR_LOCKS_RESORT_LOCK_PROPERTIES_FETCH_ENDPOINT,
        DOOR_LOCKS_RESORT_LOCK_PROPERTY_BY_ID_ENDPOINT,
    } from '../../../../endpoints';
    import { formatResponseError } from '../../../../utils/api';
    import { truncate } from '../../../../utils/strings';
    import Grid from '../../../generic/grid/grid.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import DisconnectModal from '../common/disconnect-modal.vue';
    import FetchLodgixPropertiesModal from '../common/fetch-lodgix-properties-modal.vue';
    import SettingsModalForm from '../common/settings-modal-form.vue';
    import ResortLockLockListModal from './resort-lock-lock/resort-lock-lock-list-modal.vue';
    import ResortLockPropertyDetailModalForm from './resort-lock-property-detail-modal-form.vue';
    import LockCellRenderer from './lock-cell-renderer.vue';

    export default {
        name: 'ResortLockSettings',
        components: {
            DisconnectModal,
            FetchLodgixPropertiesModal,
            Grid,
            ResponsiveButton,
            SettingsModalForm,
            ResortLockPropertyDetailModalForm,
            ResortLockLockListModal,
            actionsCellRenderer: ActionsCellRenderer,
            lockCellRenderer: LockCellRenderer,
        },
        mounted() {
            this.load();
        },
        data() {
            return {
                busy: false,
                disconnectModalID: 'resort-lock-disconnect-modal',
                updatePropertyModalID: 'resort-lock-property-update-modal',
                lockListModalID: 'resort-lock-locks-modal',
                settingsModalID: 'resort-lock-settings-modal',
                resortLockProperties: [],
                currentResortLockProperty: null,
                fetchLodgixPropertiesModalKey: 0,
                fetchLodgixPropertiesModalID: 'resort-lock-fetch-lodgix-properties-modal',
                columnsDefs: [
                    {
                        field: 'name',
                        headerName: 'LockState Property',
                        sort: 'asc',
                    },
                    {
                        field: 'address',
                        headerName: 'Address'
                    },
                    {
                        headerName: '',
                        maxWidth: 80,
                        minWidth: 80,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'lockCellRenderer',
                        cellRendererParams: {
                            click: (data) => {
                                this.currentResortLockProperty = data;
                                this.$bvModal.show(this.lockListModalID);
                            },
                        }
                    },
                    {
                        headerName: 'Lodgix Property',
                        valueGetter: ({ data }) => {
                            if (data) {
                                return data.rental_property_name || 'Not set';
                            }
                        }
                    },
                    {
                        headerName: 'Lock',
                        valueGetter: ({ data }) => {
                            if (data) {
                                return data.lock_name || 'Not set';
                            }
                        }
                    },
                    {
                        headerName: '',
                        maxWidth: 130,
                        minWidth: 130,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            update: (data) => {
                                this.currentResortLockProperty = data;
                                this.$bvModal.show(this.updatePropertyModalID);
                            },
                            delete: async (data, values) => {
                                this.$parent.$emit('busy', true);

                                try {
                                    await API.delete(
                                        DOOR_LOCKS_RESORT_LOCK_PROPERTY_BY_ID_ENDPOINT(data.id),
                                        { data: values }
                                    );
                                    await this.refreshResortLockProperties();
                                } catch (err) {
                                    const { generic } = formatResponseError(err);
                                    this.errorToast({ text: generic || 'Could not delete resort lock property'})
                                } finally {
                                    this.$parent.$emit('busy', false);
                                }
                            },
                        }
                    },
                ],
            }
        },
        computed: {
            ...mapGetters('doorLock', ['resortLockAccount']),
            accountEmailTruncated() {
                if (this.resortLockAccount && this.resortLockAccount.email) {
                    return truncate(this.resortLockAccount.email, 20);
                }
                return '';
            },
            currentResortLockPropertyID() {
                return !!this.currentResortLockProperty
                    ? this.currentResortLockProperty.id
                    : null;
            },
            currentResortLockPropertyName() {
                return !!this.currentResortLockProperty
                    ? this.currentResortLockProperty.name
                    : '';
            },
            currentResortLockPropertyInitialValues() {
                if (!!this.currentResortLockProperty) {
                    return {
                        id: this.currentResortLockProperty.id,
                        time_zone: this.currentResortLockProperty.time_zone,
                        lock_id: this.currentResortLockProperty.lock_id,
                        rental_property_id: this.currentResortLockProperty.rental_property_id,
                    }
                }
            },
        },
        methods: {
            handleDisconnectClick() {
                this.$bvModal.show(this.disconnectModalID);
            },
            handleSettingsClick() {
                this.$bvModal.show(this.settingsModalID);
            },
            handleFetchLodgixPropertiesClick() {
                this.fetchLodgixPropertiesModalKey += 1;
                this.$nextTick(() => {
                    this.$bvModal.show(this.fetchLodgixPropertiesModalID);
                })
            },
            handleResortLockLockListModalHide() {
                this.currentResortLockProperty = null;
            },
            async handleUpdateResortLockPropertySuccess() {
                await this.load();
                this.currentResortLockProperty = null;
            },
            async handleSyncResortLockPropertiesClick() {
                this.busy = true;
                this.$parent.$emit('busy', true);

                try {
                    await this.syncResortLockProperties();
                    await this.fetchResortLockProperties();
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
            createProperty({ id }) {
                return API.post(DOOR_LOCKS_RESORT_LOCK_PROPERTIES_ENDPOINT(), { id });
            },
            async disconnect() {
                await this.$store.dispatch('doorLock/disconnectResortLockAccount');
                this.$parent.$emit('remote-lock-redirect');
            },
            async load() {
                this.busy = true;
                this.$parent.$emit('busy', true);

                try {
                    await this.fetchResortLockProperties();
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
            async syncResortLockProperties() {
                try {
                    const response = await API.get(DOOR_LOCKS_RESORT_LOCK_PROPERTIES_FETCH_ENDPOINT());
                    const count = response.data.count;
                    this.successToast({text: `Fetched ${count} ${count === 1 ? 'property' : 'properties'}.`})
                } catch {
                    this.errorToast({ text: `Could not load resort lock properties.`})
                }
            },
            async refreshResortLockProperties() {
                this.$parent.$emit('busy', true);

                try {
                    await this.fetchResortLockProperties();
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async fetchResortLockProperties() {
                try {
                    const response = await API.get(DOOR_LOCKS_RESORT_LOCK_PROPERTIES_ENDPOINT());
                    this.resortLockProperties = response.data;
                } catch {
                    this.errorToast({text: `Could not load resort lock properties.`})
                }
            },
            async updateResortLockProperty({ id, ...payload }) {
                return API.put(DOOR_LOCKS_RESORT_LOCK_PROPERTY_BY_ID_ENDPOINT(id), payload);
            },
        }
    }
</script>

<style scoped lang="scss">
    .break {
        flex-basis: 100%;
        height: 0;
    }
</style>
